/** Prepare a photo the way the server expects: upright, longest side ≤ maxSide, JPEG (plan section 3). */

export function fitWithin(width: number, height: number, maxSide: number): { width: number; height: number } {
  const long = Math.max(width, height);
  if (long <= maxSide) return { width, height };
  const scale = maxSide / long;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

export interface PreparedPhoto {
  blob: Blob;
  width: number;
  height: number;
  previewUrl: string;
}

export class PhotoError extends Error {
  constructor(readonly code: "unsupported_photo" | "image_too_large") {
    super(code);
  }
}

const MAX_INPUT_BYTES = 40 * 1024 * 1024;

export async function preparePhoto(file: File, maxSide: number, quality: number): Promise<PreparedPhoto> {
  if (file.size > MAX_INPUT_BYTES) throw new PhotoError("image_too_large");
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new PhotoError("unsupported_photo"); // e.g. HEIC outside Safari
  }
  const size = fitWithin(bitmap.width, bitmap.height, maxSide);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new PhotoError("unsupported_photo");
  ctx.drawImage(bitmap, 0, 0, size.width, size.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob) throw new PhotoError("unsupported_photo");
  return { blob, ...size, previewUrl: URL.createObjectURL(blob) };
}
